import type { NativeContextRNProps } from "../../../shared/props/NativeContextRNProps";
import type { RNVJNavigationTrigger } from "../types/RNVJNavigationTrigger";

import { getDeviceProperties } from "../../../shared/logging/LoggableDeviceProperties";
import { logHttpError } from "../../../shared/logging/NetworkLogging";
import getRNVJNavigationEvent from "../util/getRNVJNavigationEvent";

import {
  AvroOptional,
  log as signalsLogRNVJHttpError,
} from "@indeed/signals-schema/com/indeed/signals/schema/viewjob/RNVJHttpErrorV4";
import { useMemo, useRef, useState } from "react";

export type Status = "fatal" | "idle" | "active" | "success" | "error";

export interface QueryStateSuccess<T> {
  readonly status: "success";
  readonly data: T;
}

export interface QueryStateError {
  readonly status: "error";
  readonly error: string;
}

export interface QueryStateIdle {
  readonly status: "idle";
}

export interface QueryStateFatal {
  readonly status: "fatal";
  readonly error: string;
}

export interface QueryStateActive {
  readonly status: "active";
}

export type QueryState<T> =
  | QueryStateFatal
  | QueryStateIdle
  | QueryStateActive
  | QueryStateSuccess<T>
  | QueryStateError;

// Used to delay retries to prevent spamming it with many clicks
const RETRY_DELAY_MS = 1000;

export interface NativeQueryOptions {
  nativeContext?: NativeContextRNProps | null;
  trigger?: RNVJNavigationTrigger | null;
  source?: string | null;
}

/**
 * This hook is intended to make web requests for fetching data.
 *
 * @param url the URL to fetch
 * @param queryBridgeFunc function to call native web request client
 * @returns the status of the query being executed
 */
export default function useNativeQuery<T>(
  provider: string,
  url: string | null | undefined,
  queryBridgeFunc: ((url: string) => Promise<T>) | undefined,
  options?: NativeQueryOptions,
): [() => Promise<void>, QueryState<T>, () => void, number | null, number | null] {
  const { nativeContext, trigger, source } = options ?? {};
  const [queryState, setQueryState] = useState<QueryState<T>>(
    queryBridgeFunc == null
      ? {
          status: "fatal",
          error: "ViewJobModule not initialized",
        }
      : {
          status: "idle",
        },
  );
  const fetchStartTimeMsRef = useRef<number | null>(null);
  const fetchEndTimeMsRef = useRef<number | null>(null);

  const getViewJobData = useMemo(
    () => async (): Promise<void> => {
      if (!url) {
        return;
      }

      fetchStartTimeMsRef.current = Date.now();

      // Note that this is fake type-safety. We are putting the responsibility
      // on the developer to ensure the result complies.
      try {
        const result: T | undefined = await queryBridgeFunc?.(url);
        if (result != null) {
          fetchEndTimeMsRef.current = Date.now();
          setQueryState({
            status: "success",
            data: result,
          });
        } else {
          // log the uncatched error, use -1 to differentiate from the catched error
          logHttpError(provider, url, "Unknown error", -1, nativeContext);
          setQueryState({
            status: "error",
            error: "Empty result",
          });
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : "Unexpected failure";
        // status code is hardcoded to 0 until we have access to it
        logHttpError(provider, url, message, 0, nativeContext);
        signalsLogRNVJHttpError({
          deviceProperties: getDeviceProperties(),
          url,
          message,
          statusCode: 0,
          ctk: AvroOptional.string(nativeContext?.ctk),
          accountId: AvroOptional.string(nativeContext?.accountId),
          urlNavigationEvent: getRNVJNavigationEvent(url, trigger, source),
        });
        setQueryState({
          status: "error",
          error: message,
        });
      }
    },
    [nativeContext, provider, queryBridgeFunc, source, trigger, url],
  );

  const retryRequest = useMemo(
    () => () => {
      setQueryState({
        status: "idle",
      });
      fetchStartTimeMsRef.current = null;
      fetchEndTimeMsRef.current = null;
      setTimeout(() => {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises -- FIXME
        getViewJobData();
      }, RETRY_DELAY_MS);
    },
    [getViewJobData],
  );

  return [
    getViewJobData,
    queryState,
    retryRequest,
    fetchStartTimeMsRef.current,
    fetchEndTimeMsRef.current,
  ];
}
